// SAS language support for CodeMirror 6 via StreamLanguage.
//
// StreamLanguage calls back per-line; for each line we lex the full line
// through kolilang_lex and dispense classifications. This is cheap (our lexer
// is linear and lines are short) and avoids writing a Lezer grammar.
//
// /* ... */ block comments can span lines, so we track inBlockComment in
// the stream state and short-circuit the lexer for pure-continuation lines.

import { StreamLanguage, StreamParser } from '@codemirror/language';
import type { Token, Kolilang } from './wasm';

type StreamState = {
  inBlockComment: boolean;
  // Cached classifications for the current line, keyed by column offset.
  // Populated on the first token request of a line, consumed as the stream
  // walks through. Simplification: re-lex the full line each time token() is
  // called while advancing the stream.
  tokens?: Token[];
  idx?: number;
  lineStart?: number;
};

function kindToTag(k: string): string | null {
  if (k.startsWith('kw_')) return 'keyword';
  switch (k) {
    case 'identifier': return 'variableName';
    case 'number': return 'number';
    case 'string':
    case 'date_lit':
    case 'time_lit':
    case 'datetime_lit':
    case 'hex_lit':
    case 'bit_lit':
      return 'string';
    case 'line_comment':
    case 'block_comment':
      return 'comment';
    case 'percent':
    case 'amp':
      return 'meta';
    case 'invalid':
      return 'invalid';
    case 'eq':
    case 'neq':
    case 'lt':
    case 'gt':
    case 'leq':
    case 'geq':
    case 'plus':
    case 'minus':
    case 'star':
    case 'slash':
    case 'caret':
    case 'concat':
    case 'question':
      return 'operator';
    case 'semi':
    case 'comma':
    case 'lparen':
    case 'rparen':
    case 'lbrace':
    case 'rbrace':
    case 'lbracket':
    case 'rbracket':
    case 'dot':
    case 'colon':
    case 'dollar':
      return 'punctuation';
    default:
      return null;
  }
}

export function sasLanguage(kolilang: Kolilang) {
  const parser: StreamParser<StreamState> = {
    name: 'sas',
    // SAS only has block comments (`/* ... */`); the older `*` …`;`
    // line-comment is awkward to toggle (it spans to next semicolon)
    // so we don't expose it. CodeMirror's toggleComment falls back to
    // wrapping the selection in `/* */` when only block tokens are
    // defined.
    languageData: {
      commentTokens: { block: { open: '/*', close: '*/' } },
    },
    startState: () => ({ inBlockComment: false }),
    copyState: (s) => ({ ...s, tokens: s.tokens ? [...s.tokens] : undefined }),

    token(stream, state) {
      // Continuation line of a multi-line block comment: swallow until `*/`.
      if (state.inBlockComment) {
        const rest = stream.string.slice(stream.pos);
        const close = rest.indexOf('*/');
        if (close < 0) {
          stream.skipToEnd();
          return 'comment';
        }
        stream.pos += close + 2;
        state.inBlockComment = false;
        return 'comment';
      }

      // Re-lex the line on first call for this line.
      if (!state.tokens || state.lineStart !== stream.start || state.idx === state.tokens.length) {
        // Only re-lex when we are actually at the start of a new scan position.
        // StreamLanguage sometimes retries; keep this simple.
      }

      // Lex the remainder of the line starting at stream.pos.
      const lineText = stream.string;
      if (stream.pos === 0 || !state.tokens || state.lineStart !== 0) {
        try {
          state.tokens = kolilang.lex(lineText).tokens;
        } catch {
          state.tokens = [];
        }
        state.idx = 0;
        state.lineStart = 0;
      }

      if (!state.tokens || state.idx === undefined) {
        stream.skipToEnd();
        return null;
      }

      // Skip whitespace the lexer ignored.
      if (stream.eatSpace()) return null;

      // Find next token at or after stream.pos.
      const arr = state.tokens;
      while (state.idx < arr.length && arr[state.idx].e <= stream.pos) state.idx += 1;
      if (state.idx >= arr.length) {
        stream.skipToEnd();
        return null;
      }
      const tok = arr[state.idx];
      if (tok.s > stream.pos) {
        // skip past any non-tokenised chars to align with the next token
        stream.pos = tok.s;
      }

      // Detect block comments that might continue past the line end.
      if (tok.k === 'block_comment') {
        const text = lineText.slice(tok.s, tok.e);
        if (!text.endsWith('*/')) {
          state.inBlockComment = true;
          stream.skipToEnd();
          return 'comment';
        }
      }

      stream.pos = tok.e;
      state.idx += 1;
      return kindToTag(tok.k);
    },

    blankLine(state) {
      if (!state.inBlockComment) {
        state.tokens = undefined;
        state.idx = undefined;
        state.lineStart = undefined;
      }
    },
  };

  return StreamLanguage.define(parser);
}
